import $            from 'jquery';
import extend       from '../util/extend';
import EventEmitter from './event-emitter';

/**
 * Step class
 */

export default class Step extends EventEmitter {

  /**
   * Step constructor
   *
   * Must be initialized with:
   *
   * $el – a root jQuery object
   *
   * @param  {Object} config – configuration data
   * @return {Step}
   */

  constructor( config = {} ) {
    super();

    extend( this, config );

    if ( !( this.$el instanceof $ ) ) {
      const msg = `this.$el must be a jQuery object, got ${ this.$el }`;
      throw new TypeError( msg );
    }

    if ( this.events && typeof this.events === 'object' ) {
      Object.keys( this.events ).forEach( evt => {
        const name = this.events[ evt ];
        const isString = typeof name === 'string';

        if ( isString ) {
          name.split(' ').forEach( cb => {
            this.on( evt, this[ cb ].bind( this ) );
          });
        } else {
          this.on( evt, name.bind( this ) );
        }
      });
    }

    this.localState = {};
  }

  /**
   * Stub template function
   *
   * Subclasses and instances should override to return a string of markup
   *
   * @param  {Object} state – redux-lite state
   * @return {String}       – markup string
   */

  template() {
    const msg = 'template() must be overridden to return a string';
    throw new TypeError( msg );
  }

  /**
   * Render function
   *
   * @param  {Object} state – redux-lite state
   * @return {Step}
   */

  render( state = {} ) {
    this.$el.html( this.template( state ) );
    return this.emit( 'render', state );
  }

  /**
   * Get property value from state object.
   * Input names can contain periods, which indicates
   * a nested value in the state, hence the need for
   * this function.
   *
   * @param {String} name - name of input {Ex: first_name or billing.first_name}
   * @param {Object} state - redux-lite state object
   * @return - value from state. Returns undefined if it doesn't exist in state
   */

  getValFromState( name, state ) {
    const target = name.split('.');
    const prop = target.pop();

    if ( target.length ) {
      target.forEach( key => {
        state = state[ key ];
      });
    }

    return state && state[ prop ];
  }

  /**
   * Run validations
   *
   * @param  {Object}  state      – redux-lite state
   * @param  {Boolean} showErrors – show errors on failed inputs?
   * @return {Boolean}            – whether or not the state is valid
   */

  isValid( state = {}, showErrors = false, rules = this.rules ) {

    return Object.keys( rules || [] ).reduce( ( prev, name ) => {
      const $input = $( `[name="${ name }"]` );
      const isVisible = $input.is(':visible');
      let valid = true;

      if ( isVisible ) {
        const value = this.getValFromState( name, state );
        valid = this.validateProp( name, value );
      }

      if ( isVisible && !valid && showErrors ) {
        $input.addClass('error');
      }

      return prev && valid;
    }, true );

  }

  /**
   * Validate a single prop
   *
   * @param  {String} name  – prop name
   * @param  {Any}    value – property value
   * @return {Boolean}      – is the value valid
   */

  validateProp( name, value ) {

    if ( this.rules && this.rules[ name ] ) {
      const rule = this.rules[ name ];
      if ( rule instanceof RegExp ) {
        return value !== null && value !== undefined && rule.test( value );
      } else {
        return rule( value );
      }
    }

    return true;
  }

  /**
   * Set state locally for an individual step
   *
   * @param {Object} newState - new state to be added, nested properties
   *  are represented by strings with periods.
   *    i.e. { 'billing.name': 'some val' }
   *    will be added as { billing: { name: 'some val' }
   *
   * @return {Object} - object containing the updated state
   */

  setState( newState ) {
    const result = Object.assign( {}, this.localState );

    Object.keys( newState ).forEach( key => {
      const props  = key.split('.');
      const last   = props.pop();
      let target = result;

      props.forEach( prop => {
        target = target[ prop ] || ( target[ prop ] = {} );
      });

      target[ last ] = newState[ key ];
    });

    return this.localState = result;
  }

  /**
   * Dispatch a redux-lite action
   *
   * The owning FormSequence will listen to this event and forward the
   * action to a redux-lite store
   *
   * @param  {Object} action – redux-lite action
   * @return {Step}
   */

  dispatch( action ) {
    return this.emit( 'dispatch', action );
  }

}
